/**
 * Cart Mutation Queue - Prevents 429 rate limiting when rapidly changing cart
 * - Serializes cart mutation requests (one at a time)
 * - Adds a tiny spacing delay only when requests are back-to-back
 * - Retries temporary cart API failures with exponential backoff
 */
(function () {
  'use strict';

  let queue = Promise.resolve();
  let lastMutationAt = 0;
  const MIN_MUTATION_GAP_MS = 250;
  const RETRY_DELAYS = [2000, 4000]; // ms before retry 1 and 2
  const RETRYABLE_STATUSES = new Set([429, 503]);

  const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

  window.cartMutationQueue = function (fn) {
    const run = async () => {
      const elapsed = Date.now() - lastMutationAt;
      if (elapsed < MIN_MUTATION_GAP_MS) {
        await sleep(MIN_MUTATION_GAP_MS - elapsed);
      }

      try {
        return await fn();
      } finally {
        lastMutationAt = Date.now();
      }
    };

    queue = queue.then(run, run);
    return queue;
  };

  window.cartAddQueue = window.cartMutationQueue;

  /**
   * Fetch with retry - pass a function that returns fetch() so body can be recreated on retry
   * @param {() => Promise<Response>} fetchFn - function that performs the fetch (recreate FormData/body each call)
   * @returns {Promise<Response>}
   */
  window.cartAddFetchWithRetry = async function (fetchFn) {
    let lastResponse;
    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
      lastResponse = await fetchFn();
      if (!RETRYABLE_STATUSES.has(lastResponse.status)) return lastResponse;
      if (attempt < RETRY_DELAYS.length) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
      }
    }
    return lastResponse;
  };
})();
